<template>
  <div class="widget__content-center widget__content-center--flex">
    <div class="widget-viz__visits">
      <template v-if="chartData">
        <div class="widget__bignumber">{{ formatNumber(chartData.Visits, 0) }}</div>
        <div class="widget__label widget__label-thin">Engaged Visits</div>

        <div class="widget-visits__engaged">
          by <span>{{ formatNumber(chartData.Visitors, 0) }}</span> Engaged Visitors
        </div>

        <div class="widget-visits__highest">
          <div class="widget__label"><IconHighest /> Highest</div>
          <div class="widget-visits__details">
            <span> <IconCalendar />{{ chartData.HighestDay }} </span>
            <span class="widget-visits__highest__count">
              <IconVisitors /> {{ formatNumber(chartData.HighestCount, 0) }} Visits
            </span>
          </div>
        </div>
        <div class="widget-visits__lowest">
          <div class="widget__label"><IconLowest /> Lowest</div>
          <div class="widget-visits__details">
            <span> <IconCalendar />{{ chartData.LowestDay }} </span>
            <span class="widget-visits__highest__count">
              <IconVisitors /> {{ formatNumber(chartData.LowestCount, 0) }} Visits
            </span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { WidgetFilter, WidgetSetting, WidgetTypeVisits } from '@/types/Widget';
import IconHighest from '@/icons/analytics/IconHighest.svg?component';
import IconCalendar from '@/icons/analytics/IconCalendar.svg?component';
import IconVisitors from '@/icons/analytics/IconVisitors.svg?component';
import IconLowest from '@/icons/analytics/IconLowest.svg?component';
import { formatNumber } from '@/utils/formatNumbers';
const props = defineProps<{
  widget: WidgetTypeVisits;
  area?: WidgetFilter;
  time?: WidgetFilter;
  zone?: WidgetSetting;
}>();

const chartData = computed(() => {
  const areaName = props.area?.name;
  const zoneId = props.zone?.id;

  if (props.widget.data) {
    if (areaName === 'Site') {
      return props.widget.data['Site']['Total'];
    }
    if (areaName === 'Zone' && zoneId) {
      return props.widget.data['Zone'][zoneId]['Total'];
    }
  }

  return null;
});
</script>

<style lang="scss">
.widget-viz__visits {
  width: 100%;

  .widget-visits__engaged {
    @include sizing.margin('xxs', 'auto');
    color: variables.$vantage-base-primitives-color-tertiary-400;
    text-align: center;

    span {
      @include sizing.padding('xxs');
      background: variables.$vantage-base-primitives-color-blue-300;
      border-radius: variables.$vantage-base-sizing-xxxs;
      color: variables.$vantage-base-primitives-color-background-100;
      font-weight: bold;
    }
  }

  .widget-visits__lowest,
  .widget-visits__highest {
    @include sizing.padding('lg');
    background: variables.$vantage-base-primitives-color-background-500;
    margin-left: -(variables.$vantage-base-sizing-xs);
    margin-right: -(variables.$vantage-base-sizing-xs);

    .widget__label {
      @include sizing.padding('xxxs', 0);
      color: black;
      font-weight: 500;
      gap: variables.$vantage-base-sizing-xxxs;
      justify-content: flex-start;
    }
  }

  .widget-visits__highest {
    border-top: 1px solid variables.$vantage-base-primitives-color-border-100;
    border-bottom: 1px solid variables.$vantage-base-primitives-color-border-200;
    margin-top: variables.$vantage-base-sizing-xl;
  }

  .widget-visits__lowest {
    border-bottom-left-radius: variables.$vantage-base-sizing-xs;
    border-bottom-right-radius: variables.$vantage-base-sizing-xs;
    margin-bottom: -(variables.$vantage-base-sizing-xs);
  }

  .widget-visits__details {
    @include mixins.flex-center(
      $horiz: flex-start,
      $gap: variables.$vantage-base-sizing-sm,
      $wrap: true
    );
    color: variables.$vantage-base-primitives-color-tertiary-500;
    margin-top: variables.$vantage-base-sizing-xxxs;
    row-gap: variables.$vantage-base-sizing-xxs;

    span {
      @include mixins.flex-center($horiz: flex-start, $gap: variables.$vantage-base-sizing-xxxs);
    }

    .widget-visits__highest__count {
      @include sizing.padding('xxxs', 'xxs');
      background: variables.$vantage-base-primitives-color-background-300;
      border-radius: variables.$vantage-base-sizing-xs;
      font-weight: 400;
    }
  }
}
</style>
